import { For, ParentProps, Show, Signal, createContext, createEffect, createSignal, onMount } from 'solid-js'
import { requireContext } from 'common'
import { lunchCounts as api, GetLunchOrderInfoResponse, StudentLunchOrder } from 'pxp-app/api'
import { LunchCountsContext } from './LunchCountsContext'
import styles from './pxp-lunch-counts.module.css'
import { Select, ST } from 'st-app'
import { untrack } from 'solid-js/web'

const LunchCountsContextJsx = createContext<LunchCountsContext>();
export const requireLunchCountsContext = () => {
    return requireContext(LunchCountsContextJsx);
}

export class LunchCountsProps {
    AGU: string = ''
}

export default function LunchCounts(props: ParentProps<LunchCountsProps>) {
    const context = new LunchCountsContext();

    return (
        <LunchCountsContextJsx.Provider value={context}>
            <LunchCountsInternal {...props} />
        </LunchCountsContextJsx.Provider>
    )
}

type dropdownOptionOrder = Omit<StudentLunchOrder, "mealOptionID" | "mealGU"> & { mealGU: Signal<string>, mealOptionID: Signal<string> }

const parseDateFromString = (dateString?: string): Date | undefined => {
    let date: Date | undefined = undefined;
    if (dateString) {
        const [monthA, dayA, yearA] = dateString.split('/') || '';
        date = new Date(parseInt(yearA), parseInt(monthA) - 1, parseInt(dayA));
    }
    return date;
}

const CompareDueDate = (dateA?: Date, dateB?: Date): number => {
    let res = NaN;
    if (dateA && dateB) {
        dateA.setHours(0, 0, 0, 0);
        dateB.setHours(0, 0, 0, 0);
        res = dateA.getTime() - dateB.getTime();
        res = res < 0 ? -1 : res > 1 ? 1 : 0;
    }
    return res;
}

const getMonthName = (month: string) => {
    switch (month) {
        case '01': return 'JAN';
        case '02': return 'FEB';
        case '03': return 'MAR';
        case '04': return 'APRIL';
        case '05': return 'MAY';
        case '06': return 'JUNE';
        case '07': return 'JULY';
        case '08': return 'AUG';
        case '09': return 'SEPT';
        case '10': return 'OCT';
        case '11': return 'NOV';
        case '12': return 'DEC';
        default: '';
    }
}

function LunchCountsInternal(props: ParentProps<LunchCountsProps>) {
    onMount(async () => {
        fetchLunchData();
    })

    const [lunchOrderData, setLunchOrderData] = createSignal<GetLunchOrderInfoResponse>()
    const [currentOrder, setCurrentOrder] = createSignal<dropdownOptionOrder>()
    const [futureOrders, setFutureOrders] = createSignal<dropdownOptionOrder[]>([])
    const [afterCutOff, setAfterCutOff] = createSignal<boolean>(false)
    const [cutOffDateTime, setcutOffDateTime] = createSignal<Date>()
    
    let loading = false;
    let requestQueue: { order: dropdownOptionOrder, mealOption: string }[] = [];

    const fetchLunchData = async () => {
        const data = await api.LoadLunchOrders(props.AGU);
        if (data) {
            let sortedOrders = data.lunchOrdersForStudent?.studentLunchOrders?.sort((a, b) => CompareDueDate(parseDateFromString(a.mealDate), parseDateFromString(b.mealDate)));
            let dates = data.validSchoolDays.map(item => ({ mealDate: item.split(' ')[0] } as StudentLunchOrder)).slice(0);
            let currentOrderObj = { mealDate: dates[0].mealDate, mealGU: createSignal<string>(''), mealOptionID: createSignal<string>('') } as dropdownOptionOrder;
            let orders: dropdownOptionOrder[] = dates.slice(1).map(item => {
                for (const orderMade of sortedOrders) {
                    if (orderMade.mealDate == item.mealDate) {
                        let dropdownOrder = orderMade as Omit<StudentLunchOrder, "mealOptionID" | "mealGU"> as dropdownOptionOrder
                        dropdownOrder.mealOptionID = createSignal<string>(orderMade.mealOptionID ?? '');
                        dropdownOrder.mealGU = createSignal<string>(orderMade.mealGU ?? '');
                        return dropdownOrder;
                    }
                }
                return { mealDate: item.mealDate, mealGU: createSignal<string>(''), mealOptionID: createSignal<string>('') } as dropdownOptionOrder
            });

            for(const orderMade of sortedOrders){
                if (orderMade.mealDate == currentOrderObj.mealDate) {
                    let dropdownOrder = orderMade as Omit<StudentLunchOrder, "mealOptionID" | "mealGU"> as dropdownOptionOrder
                    dropdownOrder.mealOptionID = createSignal<string>(orderMade.mealOptionID ?? '');
                    dropdownOrder.mealGU = createSignal<string>(orderMade.mealGU ?? '');
                    currentOrderObj = dropdownOrder;
                }
            }

            if (data.cutOffTime) {
                //Remove specific date from cut off time
                const parsedDate = new Date(data.cutOffTime);
                const newDate = new Date();
                newDate.setHours(parsedDate.getHours(), parsedDate.getMinutes(), 0, 0);
                setcutOffDateTime(newDate)

                if (cutOffDateTime()) {
                    setAfterCutOff(new Date() > cutOffDateTime()!);
                }
            }

            setLunchOrderData(data);
            setFutureOrders(orders);
            setCurrentOrder(currentOrderObj);

            [currentOrder(), ...futureOrders()].forEach((order) => {
                let firstTime = true;
                createEffect(() => {
                    const [mealOptionID] = order!.mealOptionID[0]();

                    if (firstTime) {
                        firstTime = false;
                    } else {
                        requestQueue.push({ order: order!, mealOption: mealOptionID })
                        processQueue();
                    }
                })
            })
        }
    }

    const processQueue = async () => {
        if (requestQueue.length > 0 && !loading) {
            loading = true;
            const nextRequest = requestQueue.shift();
            if (nextRequest) {
                const mealOption = nextRequest.mealOption;
                const mealGU = untrack(nextRequest.order.mealGU[0])
                let returnedMealGU = await updateOrder(
                    ({
                        ...nextRequest.order,
                        mealGU: mealGU,
                        mealOptionID: mealOption,
                        delete: mealOption == '' || mealOption == undefined,
                        mealText: lunchOrderData()?.lunchOrdersListForSchool.lunchOrderList.find((item => mealOption === item.mealOptionID.toString()))?.orderDescription
                    } as StudentLunchOrder));
                if (returnedMealGU) {
                    nextRequest.order.mealGU[1](returnedMealGU);
                }
                loading = false;
                processQueue();
            }
        }
    };

    const updateOrder = async (order: StudentLunchOrder) => {
        try {
            const data = await api.UpdateLunchOrder(order, props.AGU);
            return data.mealGU ?? ''
        } catch (e) {
            ST.ShowMessage(e).done(() => {
                if (cutOffDateTime()) {
                    setAfterCutOff(new Date() > cutOffDateTime()!)
                }
            });
        }
        return '';
    }

    return (
        <div class={styles['pxp-lunch-counts']}>
            <div class={`panel panel-default ${styles['pxp-lunch-panel']}`}>
                <h2 class='panel-heading'>{(window as any).PXP.Translations.LunchCount.TodayOrder || 'Today\'s Order'}</h2>
                <div class='panel-body'>
                    <h2>{afterCutOff() ? ((window as any).PXP.Translations.LunchCount.SelectionPassed || 'Locked: Selection time passed') : (((window as any).PXP.Translations.LunchCount.OrderBy+': ' || 'Order by: ') + new Date(lunchOrderData()?.cutOffTime!).toLocaleTimeString(undefined,
                        {
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true
                        }))}</h2>
                    <Show when={currentOrder() !== undefined}>
                        <table class='table table-striped' style={{ "margin-bottom": 0 }}>
                            <tbody>
                                <tr>
                                    <td>
                                        <div class={styles['pxp-lunch-future-order-row']}>
                                            <div class={styles['pxp-lunch-future-order-date']}>
                                                {getMonthName(currentOrder()!.mealDate?.split('/')[0] ?? '') + ' ' + parseInt(currentOrder()!.mealDate?.split('/')[1] ?? '')}
                                            </div>
                                            <div style={{ width: '120px' }}>
                                                <Show when={afterCutOff() == false} fallback={<>{currentOrder()?.mealText ?? ((window as any).PXP.Translations.LunchCount.NoOrder || 'No Order Placed for Today')}</>}>
                                                    <Select value={currentOrder()!.mealOptionID} displayValue={<>{lunchOrderData()?.lunchOrdersListForSchool.lunchOrderList.find((item => currentOrder()!.mealOptionID[0]() === item.mealOptionID.toString()))?.orderDescription}</>}>
                                                        <For each={[{ mealOptionID: '', orderDescription: '' }, ...(lunchOrderData()?.lunchOrdersListForSchool.lunchOrderList ?? [])]}>
                                                            {((option) =>
                                                                <Select.Option value={option.mealOptionID}>{option.orderDescription}</Select.Option>
                                                            )}
                                                        </For>
                                                    </Select>
                                                </Show>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </Show>
                </div>
            </div>
            <Show when={futureOrders() !== undefined && futureOrders().length > 0 && lunchOrderData() !== undefined}>
                <div class={`panel panel-default ${styles['pxp-lunch-panel']}`}>
                    <h2 class='panel-heading'>{(window as any).PXP.Translations.LunchCount.FutureOrder || 'Future Orders'}</h2>
                    <div class='panel-body'>
                        <table class='table table-striped' style={{ "margin-bottom": 0 }}>
                            <tbody>
                                {futureOrders().map((futureOrder, rowIndex) => (
                                    <tr>
                                        <td>
                                            <div class={styles['pxp-lunch-future-order-row']}>
                                                <div class={styles['pxp-lunch-future-order-date']}>
                                                    {getMonthName(futureOrder.mealDate?.split('/')[0] ?? '') + ' ' + parseInt(futureOrder.mealDate?.split('/')[1] ?? '')}
                                                </div>
                                                <div style={{ width: '120px' }}>
                                                    <Select value={futureOrder.mealOptionID} displayValue={<>{lunchOrderData()?.lunchOrdersListForSchool.lunchOrderList.find((item => futureOrder.mealOptionID[0]() === item.mealOptionID.toString()))?.orderDescription}</>}>
                                                        <For each={[{ mealOptionID: '', orderDescription: '' }, ...(lunchOrderData()?.lunchOrdersListForSchool.lunchOrderList ?? [])]}>
                                                            {((option) => (
                                                                <Select.Option value={option.mealOptionID}>{option.orderDescription}</Select.Option>
                                                            ))}
                                                        </For>
                                                    </Select>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </Show>
        </div>
    );
}

LunchCounts.defaultProps = new LunchCountsProps();